import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import { Dropdown, ToggleSwitch } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import { Tab, Tabs } from '@mui/material';
import Text from '@commons/Text';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_DELETE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';

import {
  API_KEY_FORM_MODE_ENUM,
  getPermissions,
  buildResources,
  permissionsConfig,
  sections,
} from './utils';

import {
  CloseButton,
  Container,
  FooterContainer,
  TabsSection,
  HeaderContainer,
  InfoCard,
  Section,
  InfoText,
  SectionTitle,
  StoreRestrictionSection,
  StoreRestrictionControls,
  Title,
  ToggleRow,
  Divider,
} from './styledComponents';
import { GeneratePublicApiFormActionButtons } from './GeneratePublicApiFormComponents/GeneratePublicApiFormActionButtons';
import { GeneratePublicApiFormActionFormInput } from './GeneratePublicApiFormComponents/GeneratePublicApiFormActionFormInput';
import { PermissionsList } from './PermissionsList';
import { publicKeysService } from '@services/backoffice/publicKeys';
import { UpdatePublicApiFormActionButtons } from './UpdatePublicApiFormComponents/UpdatePublicApiFormActionButtons';
import UpdatePublicApiFormActionFormInput from './UpdatePublicApiFormComponents/UpdatePublicApiFormActionFormInput';

const schema = yup
  .object({
    userName: yup
      .string()
      .required(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.KEY_NAME_FORMAT_ERROR'))
      .matches(
        /^[a-z0-9]+(-[a-z0-9]+)*$/i,
        i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.KEY_NAME_FORMAT_ERROR'),
      )
      .max(50),
  })
  .required();

export const GeneratePublicApiForm = ({
  isOpened = false,
  mode = API_KEY_FORM_MODE_ENUM.CREATE,
  selectedKey = null,
  stores = [],
  onClose,
  onSuccess,
  openGenericModal,
  pageLoading,
  pageLoaded,
  showSuccessMessage,
  showErrorMessage,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: { userName: '' },
  });

  const theme = getTheme();

  const [activeTab, setActiveTab] = useState(sections[0].id);
  const [permissions, setPermissions] = useState(getPermissions(mode, selectedKey));

  const [restrictToStores, setRestrictToStores] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);

  const hasAtLeastOnePermission = Object.values(permissions).some(Boolean);
  const isStoreRestrictionValid = !restrictToStores || selectedStores.length > 0;
  const isCreateFormValid = isValid && hasAtLeastOnePermission && isStoreRestrictionValid;
  const isUpdateFormValid = hasAtLeastOnePermission && isStoreRestrictionValid;

  const getRestrictedStoreIds = () => {
    if (!restrictToStores) {
      return [];
    }
    return selectedStores.map(({ id }) => id);
  };

  const onGenerateSubmit = async ({ userName }) => {
    const resources = buildResources(permissions);
    const restrictedStoreIds = getRestrictedStoreIds();
    pageLoading();
    try {
      await publicKeysService.createPublicApiKey(userName, resources, restrictedStoreIds);
      showSuccessMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.CREATE_SUCCESS'));
      onSuccess();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.CREATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const onUpdateSubmit = async () => {
    const resources = buildResources(permissions);
    const restrictedStoreIds = getRestrictedStoreIds();
    pageLoading();
    try {
      await publicKeysService.updateApiKey(selectedKey.id, resources, restrictedStoreIds);
      showSuccessMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.UPDATE_SUCCESS'));
      onSuccess();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.UPDATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const performDelete = async () => {
    pageLoading();
    try {
      await publicKeysService.deleteApiKey(selectedKey.id);
      showSuccessMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.DELETE_SUCCESS'));
      onSuccess();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.DELETE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const onDelete = () => {
    const confirmationModal = getConfirmationModal({
      title: i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.DELETE_CONFIRMATION_TITLE'),
      content: i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.DELETE_CONFIRMATION_MESSAGE'),
      icon: '/images/inpulse/trash-white.svg',
      actions: [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          ...GENERIC_MODAL_DELETE_BUTTON(),
          handleClick: performDelete,
        },
      ],
    });

    openGenericModal(confirmationModal);
  };

  useEffect(() => {
    if (!isOpened) {
      return;
    }
    setActiveTab(sections[0].id);
    setPermissions(getPermissions(mode, selectedKey));
    if (mode === API_KEY_FORM_MODE_ENUM.CREATE) {
      reset({ userName: '' });
    }
  }, [isOpened, mode, selectedKey, reset]);

  useEffect(() => {
    if (!isOpened) {
      setRestrictToStores(false);
      setSelectedStores([]);
      return;
    }

    const restrictedStoreIds = selectedKey?.restrictedStoreIds ?? [];
    setRestrictToStores(restrictedStoreIds.length > 0);
    setSelectedStores(stores.filter(({ id }) => restrictedStoreIds.includes(id)));
    // `stores` (Redux active stores) is loaded app-wide and stable; depending on it
    // would re-seed and wipe the user's selection on unrelated store-list updates.
  }, [isOpened, selectedKey]);

  return (
    <Container>
      <HeaderContainer>
        <Title>
          {mode === API_KEY_FORM_MODE_ENUM.CREATE
            ? i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.GENERATE_NEW_API_KEY')
            : selectedKey.lastName}
        </Title>

        <CloseButton type="button" onClick={onClose}>
          <img alt="" height={32} src="/images/inpulse/close-black-small.svg" width={24} />
        </CloseButton>
      </HeaderContainer>

      <Divider />

      <Section>
        {mode === API_KEY_FORM_MODE_ENUM.CREATE ? (
          <GeneratePublicApiFormActionFormInput errors={errors} register={register} />
        ) : (
          <UpdatePublicApiFormActionFormInput selectedKey={selectedKey} />
        )}
      </Section>
      <Divider />
      <TabsSection>
        <SectionTitle>
          {i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.PERMISSIONS_TITLE')}
        </SectionTitle>
        <Tabs
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.colors.brand.primary,
              height: '3px',
              paddingLeft: 0,
            },
          }}
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          {sections.map((section) => (
            <Tab
              key={section.id}
              label={i18next.t(section.label)}
              sx={{
                font: theme.fonts.textMiddleBold,
                color: theme.colors.greys.darker,
                '&.Mui-selected': {
                  color: theme.colors.greys.darkest,
                  font: theme.fonts.textMiddleInterBold,
                },
              }}
              value={section.id}
            />
          ))}
        </Tabs>
        <PermissionsList
          permissionsConfig={permissionsConfig}
          setValues={setPermissions}
          tab={activeTab}
          values={permissions}
        />

        <Divider />
        <StoreRestrictionSection>
          <SectionTitle>
            {i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.STORE_RESTRICTION_TITLE')}
          </SectionTitle>
          <StoreRestrictionControls>
            <ToggleRow>
              <ToggleSwitch
                checked={restrictToStores}
                handleClick={() => setRestrictToStores((prev) => !prev)}
                id="restrict-to-stores"
              />
              <Text>
                {i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.STORE_RESTRICTION_TOGGLE')}
              </Text>
            </ToggleRow>
            {restrictToStores && (
              <Dropdown
                // Render the panel in-flow (not floating) so it opens below the
                // trigger and scrolls within the drawer's scrollable area instead of
                // being clipped, keeping the list and the "Apply" button reachable.
                customListStyle={{ position: 'relative' }}
                iconSrc="/images/inpulse/pin-black-small.svg"
                isUniqueSelection={false}
                items={stores}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={selectedStores}
                onSelectionChange={setSelectedStores}
              />
            )}
          </StoreRestrictionControls>
        </StoreRestrictionSection>
      </TabsSection>

      {mode === API_KEY_FORM_MODE_ENUM.CREATE && (
        <InfoCard>
          <InfoText>{i18next.t('ADMIN.INTEGRATIONS.PUBLIC_API_KEYS.INFO_BOX_TEXT')}</InfoText>
        </InfoCard>
      )}
      <Divider />
      <FooterContainer>
        {mode === API_KEY_FORM_MODE_ENUM.CREATE ? (
          <GeneratePublicApiFormActionButtons
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isValid={isCreateFormValid}
            onClose={onClose}
            onGenerateSubmit={onGenerateSubmit}
          />
        ) : (
          <UpdatePublicApiFormActionButtons
            isSubmitting={isSubmitting}
            isValid={isUpdateFormValid}
            onDelete={onDelete}
            onUpdateSubmit={onUpdateSubmit}
          />
        )}
      </FooterContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  stores: state.baseReducer.activeStores,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => dispatch(openGenericModal(params)),
  pageLoading: () => dispatch(loading()),
  pageLoaded: () => dispatch(loadingSuccess()),
  showSuccessMessage: (msg) => dispatch(showSuccessMessage(msg)),
  showErrorMessage: (msg) => dispatch(showErrorMessage(msg)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GeneratePublicApiForm);
